/**
 * Menu-group availability evaluation — the TypeScript counterpart of the POS
 * `MenuGroup.isAvailableForDisplay` and its sub-checks
 * (`apps/pos/lib/models/menus/menu_group.dart`). Kept behaviour-identical so a
 * group that the POS shows/hides at a given moment is shown/hidden identically
 * in online ordering — no POS↔web drift.
 *
 * Every function takes `now` plus the restaurant's IANA `timeZone`. A schedule
 * is restaurant wall-clock time, so it is read on the restaurant's clock — see
 * `readScheduleClock`. Omitting the zone keeps the process clock.
 *
 * Pure functions; `now` is injectable for deterministic testing.
 */

import type { OrderType } from '../orders/types';
import { readScheduleClock } from '../time/zonedClock';
import {
  MENU_GROUP_DAY_KEYS,
  type IMenuGroup,
  type IMenuGroupDateRange,
  type IMenuGroupDaySchedule,
  type IMenuGroupDaysAvailable,
  type IMenuGroupSeasonalAvailability,
  type IMenuGroupTimeWindow,
  type MenuGroupDayKey,
} from './MenuGroup';
import { allDayCarryEnd, resolveDaySchedule } from './menuGroupSchedule';
import type { SalesChannel } from './types';

/** Parse "HH:MM" to minutes since midnight, or null if invalid. Mirrors POS `_parseTimeToMinutes`. */
function parseTimeToMinutes(s: string | undefined): number | null {
  if (!s) return null;
  const parts = s.split(':');
  if (parts.length !== 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || h > 23 || m < 0 || m > 59) {
    return null;
  }
  return h * 60 + m;
}

/**
 * True if `now` falls within the seasonal window. Inactive or incompletely
 * configured → true (no restriction). Handles year-wrapping ranges (e.g.
 * Oct 15 → Feb 28). Mirrors POS `MenuGroupSeasonalAvailability.isCurrentlyAvailable`.
 */
export function isSeasonallyAvailable(
  s: IMenuGroupSeasonalAvailability | undefined,
  now: Date,
  timeZone?: string | null,
): boolean {
  if (!s || !s.isActive) return true;
  if (s.startMonth == null || s.startDay == null || s.endMonth == null || s.endDay == null) {
    return true;
  }
  const clock = readScheduleClock(now, timeZone);
  const startMD = s.startMonth * 100 + s.startDay;
  const endMD = s.endMonth * 100 + s.endDay;
  const todayMD = clock.month * 100 + clock.date;
  return startMD <= endMD
    ? todayMD >= startMD && todayMD <= endMD
    : todayMD >= startMD || todayMD <= endMD;
}

/** A date-only field's civil date as a comparable `YYYYMMDD` number. */
function civilKeyOfDateOnly(value: Date | string): number | null {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  // UTC getters: the management app stores a date-only pick as UTC midnight
  // (`new Date("YYYY-MM-DD")`). Local getters roll it back a day for every
  // reader west of UTC, so a range "Sep 14–20" ran Sep 13–19 in New York.
  return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
}

/**
 * True if `now` (date-only, inclusive) falls within the date range. Inactive or
 * incomplete → true. Mirrors POS `MenuGroupDateRange.isCurrentlyWithinRange`.
 */
export function isWithinDateRange(
  r: IMenuGroupDateRange | undefined,
  now: Date,
  timeZone?: string | null,
): boolean {
  if (!r || !r.isActive) return true;
  if (!r.startDate || !r.endDate) return true;
  const start = civilKeyOfDateOnly(r.startDate);
  const end = civilKeyOfDateOnly(r.endDate);
  if (start == null || end == null) return true;
  const clock = readScheduleClock(now, timeZone);
  const today = clock.year * 10000 + clock.month * 100 + clock.date;
  return today >= start && today <= end;
}

/**
 * The part of `windows` that is open at `minute` ON THE DAY THE WINDOWS BELONG
 * TO. An overnight window (start > end) owns only its evening here; its
 * after-midnight tail belongs to the NEXT day — see `matchesOvernightTail`.
 */
function matchesSameDay(windows: readonly IMenuGroupTimeWindow[], minute: number): boolean {
  for (const w of windows) {
    if (w.isActive === false) continue;
    const start = parseTimeToMinutes(w.startTime);
    const end = parseTimeToMinutes(w.endTime);
    if (start == null || end == null) continue;
    if (start <= end ? minute >= start && minute <= end : minute >= start) return true;
  }
  return false;
}

/** True when an overnight window from the PREVIOUS day is still open at `minute`. */
function matchesOvernightTail(windows: readonly IMenuGroupTimeWindow[], minute: number): boolean {
  for (const w of windows) {
    if (w.isActive === false) continue;
    const start = parseTimeToMinutes(w.startTime);
    const end = parseTimeToMinutes(w.endTime);
    if (start == null || end == null) continue;
    if (start > end && minute <= end) return true;
  }
  return false;
}

/**
 * True if `now` falls inside any ACTIVE window in the list, taking the list as
 * one day's windows: an overnight window matches its evening, and its
 * after-midnight tail is the next day's business. Inactive and unparseable
 * windows are skipped; a non-empty list where nothing matches is `false`.
 *
 * Exported deliberately: this was module-private, which is the reason both
 * marketplace transformers (KitchenHub, Uber Eats) reimplemented it locally and
 * then drifted. New callers must use this rather than write a third copy.
 */
export function matchesAnyWindow(
  windows: readonly IMenuGroupTimeWindow[],
  now: Date,
  timeZone?: string | null,
): boolean {
  return matchesSameDay(windows, readScheduleClock(now, timeZone).minute);
}

/**
 * True if the group's day-of-week schedule allows `now`. Inactive → true.
 * Mirrors POS `MenuGroupDaysOfWeek.isCurrentDayAllowed`.
 *
 * 🔴 OVERNIGHT WINDOWS BELONG TO THE DAY THEY START. "Mon–Fri 6 PM–4 AM" is
 * Monday 6 PM through Tuesday 4 AM, … Friday 6 PM through Saturday 4 AM — the
 * reading every operator means and the one Toast, Square and the marketplaces
 * (which receive these windows verbatim) all use. It used to stay WITHIN the
 * day: Friday's dinner stopped at midnight, and Monday 12–4 AM served Sunday
 * night on a menu Sunday never scheduled. So now is open when
 *
 *   today's schedule is open at this minute (all day, a same-day window, or an
 *   overnight window's evening), OR
 *   yesterday's schedule has an overnight window whose tail is still running, OR
 *   yesterday was all day and the group's latest overnight close
 *   (`allDayCarryEnd`) has not passed — "all day Sunday" on a 4 AM dinner menu
 *   runs to 4 AM Monday.
 *
 * Every day resolves through `resolveDaySchedule`, so `perDay` (when total) and
 * the legacy booleans + global windows are read by exactly one rule.
 */
export function isCurrentDayAllowed(
  da: IMenuGroupDaysAvailable | undefined,
  now: Date,
  timeZone?: string | null,
): boolean {
  if (!da || !da.isActive) return true;
  const clock = readScheduleClock(now, timeZone);

  const today = resolveDaySchedule(da, MENU_GROUP_DAY_KEYS[clock.weekday]!);
  if (today.isAvailable) {
    // Empty ⇒ all day, matching the legacy empty-window-collection meaning.
    if (today.windows.length === 0) return true;
    if (matchesSameDay(today.windows, clock.minute)) return true;
  }

  const yesterday = resolveDaySchedule(da, MENU_GROUP_DAY_KEYS[(clock.weekday + 6) % 7]!);
  if (!yesterday.isAvailable) return false;
  if (yesterday.windows.length === 0) {
    const week = Object.fromEntries(
      MENU_GROUP_DAY_KEYS.map((key) => [key, resolveDaySchedule(da, key)]),
    ) as Record<MenuGroupDayKey, IMenuGroupDaySchedule>;
    const carryEnd = allDayCarryEnd(week);
    return carryEnd != null && clock.minute <= carryEnd;
  }
  return matchesOvernightTail(yesterday.windows, clock.minute);
}

/**
 * Unified time-based availability — every active mechanism must allow `now`.
 * Mirrors POS `MenuGroup.isAvailableForDisplay` (seasonal + date range + days).
 */
export function isMenuGroupAvailableForDisplay(
  group: IMenuGroup,
  now: Date = new Date(),
  timeZone?: string | null,
): boolean {
  if (!isSeasonallyAvailable(group.seasonalAvailability, now, timeZone)) return false;
  if (!isWithinDateRange(group.dateRange, now, timeZone)) return false;
  if (!isCurrentDayAllowed(group.daysAvailable, now, timeZone)) return false;
  return true;
}

/**
 * The channel-visibility / service-availability fields a menu group carries, as
 * the minimum shape the channel gate needs. Declared structurally (rather than
 * as `IMenuGroup`) so the STATIC gate can be applied to the partially-hydrated
 * groups the website builder and root-selection surfaces hold.
 */
export type MenuGroupChannelFields = Pick<IMenuGroup, 'isActive' | 'isArchived'> &
  Partial<
    Pick<
      IMenuGroup,
      | 'visibleInPOS'
      | 'visibleInKiosk'
      | 'visibleInOnlineOrdering'
      | 'visibleToPartners'
      | 'visibleInInvoices'
      | 'availability'
    >
  >;

/**
 * Channel → the group flags that gate it.
 *
 * Deliberately mirrors `Item.isAvailableOnChannel` (`Item.ts:914`) member for
 * member so a group and an item answer the same question with the same
 * vocabulary — `SalesChannel` (`types.ts:303`) is the ONE channel enum, and a
 * second one is how a group ends up hidden on a surface the item is shown on.
 *
 * `SalesChannel` conflates two axes and that is load-bearing here, not an
 * accident to paper over:
 *   - CHANNEL members (POS/KIOSK/ONLINE_ORDERING/CUSTOMER_INVOICE/marketplaces)
 *     ask "which screen renders this", and read the `visibleIn*` flags.
 *   - FULFILLMENT members (DINE_IN/TAKEOUT/DELIVERY/…) ask "which order type
 *     may carry this", and read `availability.*`.
 * A caller filtering a rendered menu asks BOTH — once for the surface it is,
 * once for the order type in the cart.
 *
 * Returns `undefined` for members that carry no group-level flag (MOBILE_APP,
 * CURBSIDE, DRIVE_THRU, CATERING) — absence means "no restriction", never
 * "hidden".
 */
function channelFlagsFor(
  group: MenuGroupChannelFields,
  channel: SalesChannel,
): (boolean | undefined)[] {
  const a = group.availability;
  switch (channel) {
    // ── Channel members: which surface renders it ──────────────────────────
    case 'POS':
      return [group.visibleInPOS];
    case 'KIOSK':
      return [group.visibleInKiosk, a?.kiosk];
    case 'ONLINE_ORDERING':
      return [group.visibleInOnlineOrdering, a?.online];
    case 'CUSTOMER_INVOICE':
      return [group.visibleInInvoices];
    case 'UBER_EATS':
    case 'DOORDASH':
    case 'GRUBHUB':
    case 'POSTMATES':
    case 'CAVIAR':
    case 'SEAMLESS':
    case 'YELP':
    case 'GOOGLE':
    case 'FACEBOOK':
    case 'INSTAGRAM':
      return [group.visibleToPartners, a?.partners];
    // ── Fulfillment members: which order type may carry it ─────────────────
    case 'DINE_IN':
      return [a?.dineIn];
    case 'TAKEOUT':
      return [a?.takeout];
    case 'DELIVERY':
      return [a?.delivery];
    default:
      return [];
  }
}

/**
 * STATIC (time-independent) channel gate: active, not archived, and no flag for
 * `channel` explicitly off.
 *
 * Use where the answer must be STABLE across the day — root-menu auto-selection,
 * builder dropdowns, partner menu pushes. The time-windowed evaluator would flip
 * a selection intraday, and a marketplace push is a snapshot, not a live render.
 *
 * Every clause is `=== false`, never truthiness: the model defaults are `?? true`
 * (`MenuGroup.ts:340-344`) so an ABSENT flag is indistinguishable from an
 * enabled one, and treating absence as "off" would hide every group written
 * before a field existed.
 */
export function isMenuGroupChannelEnabled(
  group: MenuGroupChannelFields,
  channel: SalesChannel,
): boolean {
  if (group.isActive === false) return false;
  if (group.isArchived) return false;
  return channelFlagsFor(group, channel).every((flag) => flag !== false);
}

/**
 * Whether a menu group should be shown on `channel` RIGHT NOW — the static
 * channel gate plus the POS time-based availability, so a group the POS shows
 * or hides at a given moment is shown or hidden identically everywhere else.
 */
export function isMenuGroupAvailableOnChannel(
  group: IMenuGroup,
  channel: SalesChannel,
  now: Date = new Date(),
  timeZone?: string | null,
): boolean {
  if (!isMenuGroupChannelEnabled(group, channel)) return false;
  return isMenuGroupAvailableForDisplay(group, now, timeZone);
}

/**
 * Whether a menu group should be shown in CUSTOMER-FACING ONLINE ORDERING right
 * now. Behavior-identical to the pre-channel-parameterized version:
 *   - active and not archived
 *   - `visibleInOnlineOrdering` not explicitly off
 *   - `availability.online` not explicitly off
 *   - within seasonal / date / day-of-week / time windows
 */
export function isMenuGroupAvailableForOnlineOrdering(
  group: IMenuGroup,
  now: Date = new Date(),
  timeZone?: string | null,
): boolean {
  return isMenuGroupAvailableOnChannel(group, 'ONLINE_ORDERING', now, timeZone);
}

/**
 * The `SalesChannel` an ORDER's type maps to, for the fulfillment half of the
 * menu filter. `quickService` carries no group-level restriction — it is a POS
 * service mode, not a fulfillment method, so it returns `undefined` (= no
 * restriction) rather than falling through to a wrong member.
 */
export function salesChannelForOrderType(orderType: OrderType): SalesChannel | undefined {
  switch (orderType) {
    case 'dineIn':
      return 'DINE_IN';
    case 'takeOut':
      return 'TAKEOUT';
    case 'delivery':
      return 'DELIVERY';
    default:
      return undefined;
  }
}

/**
 * The `SalesChannel` a WEB CART's order type maps to. The cart carries its own
 * narrower union (`CartOrderType`) rather than `OrderType` — it has no
 * `quickService`, and `pickup` is the same fulfillment method the group flag
 * calls `takeout`. Typed structurally so `menus/` does not take a dependency
 * on `cart/`.
 *
 * `dineIn` is the guest TABLE page (Guest QR Platform, ruling R17): a guest who
 * scanned the code on their table is in the dining room, so the menu they browse
 * is the DINE_IN one. It was accepted here before the cart carried it; the cart
 * now carries it too, and both still use this one mapping.
 */
export function salesChannelForCartOrderType(
  orderType: 'pickup' | 'delivery' | 'dineIn',
): SalesChannel {
  switch (orderType) {
    case 'delivery':
      return 'DELIVERY';
    case 'dineIn':
      return 'DINE_IN';
    default:
      return 'TAKEOUT';
  }
}
